import * as ToneJS from 'tone';
import { ContainerAtom, HorizontalNoteContainer } from '../model/containers';
import {
    MetronomeMarking,
    Note,
    NoteDuration,
    NoteVelocity,
    TimeSignature
} from '../model/core';

import { CaretController } from '../utils/ScrollController';
import { RydInstrument } from '../model/instrument';
import { Metronome } from '../model/metronome';

import _ from 'lodash';
import { RecordArmController } from './RecordingController';
import { RydTrack } from '../model/tracks';
import {
    isMetronomeMuted,
    playbackConfStore
} from '../stores/PlaybackConfStore';
import { LoopPlaybackSetup } from '../model/loop';

const ACTIVE_SEQUNENCES: ToneJS.Sequence[] = [];

function createMetronomeSynth() {
    return new ToneJS.Synth({
        envelope: {
            attack: 0,
            decay: 0.05,
            sustain: 0,
            release: 0
        }
    }).toDestination();
}

// FIXME: this already exists in state.
const WebContextHolder: {
    audioContext: AudioContext | null;
} = {
    audioContext: null,
}

export function getAudioContext() {
    // Create a single AudioContext for the synth
    if (!WebContextHolder.audioContext) {
        WebContextHolder.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    return WebContextHolder.audioContext;
}
/** @deprecated */
async function playSequence(
    grid: ContainerAtom<Note>[][],
    ppq: number,
    resolution: NoteDuration,
    metronome: MetronomeMarking,
    timeSignature: TimeSignature,
    caretController?: CaretController,
    instrument?: RydInstrument,
    // if isRecording, metronome will last only for the duration of the existing sequence.
    isRecording: boolean = false,
    // The number of bars to count in before starting the metronome and caret.
    metronomeCountInBars: number = 0,
    atEnd: () => void = () => { }
) {
    await ToneJS.start();

    console.log('playSequence', grid, resolution, metronome, timeSignature, caretController, instrument, isRecording, metronomeCountInBars);

    const caretSequence = createCaretSequence(1, grid.length, caretController);
    const metronomeSequence = beatPatternToSequence(
        createMetronomeSynth(),
        Metronome.makeMetronomeBeatSequence(timeSignature),
        ppq,
        isMetronomeMuted
    );
    const currentSequence = gridToSequence(
        grid,
        instrument,
        isRecording,
        [metronomeSequence, caretSequence],
        atEnd
    );

    metronomeSequence.loop = true;
    metronomeSequence.start(0);

    caretSequence.loop = true;
    caretSequence.start(0);

    currentSequence.loop = false;
    currentSequence.start(0);

    ACTIVE_SEQUNENCES.push(currentSequence);
    ACTIVE_SEQUNENCES.push(metronomeSequence);
    ACTIVE_SEQUNENCES.push(caretSequence);

    ToneJS.getTransport().PPQ = ppq;
    ToneJS.getTransport().bpm.value = metronome.bpm;
    ToneJS.getTransport().start();
};

/** @deprecated */
const playMetronome = async (
    pattern: Metronome.BeatPattern,
    metronome: MetronomeMarking,
    ppq: number,
) => {

    await ToneJS.start();

    const metronomeSequence = beatPatternToSequence(createMetronomeSynth(), pattern, ppq);
    // metronome loops
    metronomeSequence.loop = true;
    metronomeSequence.start(0);

    ToneJS.getTransport().bpm.value = metronome.bpm;
    ToneJS.Transport.start();
};

const stopSequence = () => {
    for (let i = ACTIVE_SEQUNENCES.length - 1; i >= 0; i--) {

        const seq = ACTIVE_SEQUNENCES.pop();
        if (!seq) continue;
        seq.stop();
        seq.cancel();
        // seq.dispose();
    }

    if (ToneJS.getTransport().state === 'started') {
        ToneJS.getTransport().stop();
    }
};

const stopMetronome = () => {
    ToneJS.Transport.stop();
};

export function pauseSequence() {
    ToneJS.Transport.pause();
}

export function continueSequence() {
    ToneJS.Transport.start();
}

export {
    playSequence,
    stopSequence,
    playMetronome,
    stopMetronome
};

function gridToSequence(
    grid: ContainerAtom<Note>[][],
    instrument?: RydInstrument,
    isRecording: boolean = false,
    dependentSequences?: ToneJS.Sequence[],
    atEnd: () => void = () => { }
): ToneJS.Sequence {

    const noteSequence: {
        dummy: boolean,
        // FIXME: remvoe this END hack
        notes: Note[] | 'END',
        duration: string,
        velocity: NoteVelocity,
        start: number
    }[][] = grid.map((ng, i) => {
        /** ToneJs will only trigger steps if there is some content in the notes array.
         * In order to allow for smooth scrolling on playback, a dummy note is added to the
         * sequence and checked explicitly for on playback.
         */
        if (!ng || ng.length == 0) return [{ dummy: true, start: i, notes: [], duration: '0', velocity: 0 }];

        const groupsWithSameDuration = _.groupBy(ng, (n) => n.duration.value);

        return _.map(groupsWithSameDuration, ng => {
            const d = `${ng[0].duration.value}i`;
            return {
                dummy: false,
                notes: ng.map((n) => n.payload as Note),
                duration: d,
                velocity: ng[0].velocity,
                start: i
            };
        });
    });

    return new ToneJS.Sequence(
        (time, note) => {
            if (note.notes === 'END') return;
            if (note.dummy) return;
            if (!note.notes || note.notes.length === 0) return;
            instrument?.triggerInSequence(note.notes, note.duration, note.velocity, time);
        },
        noteSequence, //.concat([{dummy: true, notes: 'END', duration: '0', start: grid.length, velocity: 0}]),
        '1i'
    );
}

function beatPatternToSequence(
    metronomeSynth: ToneJS.Synth,
    pattern: Metronome.BeatPattern,
    ppq: number,
    isMuted: () => boolean = () => false,
): ToneJS.Sequence {
    const metronomeActivityObserver = Metronome.ActivityObserver.getInstance();

    const noteSequence = pattern.map((beat, i) => {
        return {
            notify: beat === 'strong',
            note: Metronome.beatStrengthToNote(beat),
            duration: `${0 | (ppq / 16)}i`,
            start: i
        };
    });

    let step = 0;
    return new ToneJS.Sequence(
        (time, note) => {
            if (note.notify) {
                metronomeActivityObserver.updateBar(step++);
            }
            if (isMuted()) return;
            if (note.note === undefined) return;
            metronomeSynth.triggerAttackRelease(note.note.getName(), note.duration, time)
        },
        noteSequence,
        '4n'
    );
}

function createCaretSequence(
    ppq: number,
    limit: number,
    caretController?: CaretController,
): ToneJS.Sequence {

    if (!caretController) {
        console.warn('CaretController is not provided, caret sequence will not scroll');
        return new ToneJS.Sequence(() => { }, [], '4n');
    }

    const ppqDuration = ppq / 8;
    const tickDuration = `${ppqDuration}i`;
    return new ToneJS.Sequence(
        (time, step) => {
            caretController?.scrollToTick(step);
        },
        Array(0 | (limit / ppqDuration)).fill(0).map((_, i) => i * ppqDuration),
        tickDuration
    );
}

export type RydPlayerSession = {
    sequences: ToneJS.Sequence[],
    start: () => void,
    pause: () => void,
    stop: () => void,
    isPlaying: () => boolean,
};

function getAndAssertPPQ(tracks: { container: HorizontalNoteContainer }[]): number {
    if (tracks.length === 0) {
        throw new Error('No tracks provided to get PPQ');
    }
    const ppq = tracks[0].container.getPPQ();
    if (!ppq || ppq <= 0) {
        throw new Error('Invalid PPQ value');
    }
    return ppq;
}

function createCurrentTickCaptureSequence(): ToneJS.Sequence {

    const sequence = new ToneJS.Sequence(
        (time, step) => {
            const recordingController = RecordArmController.getInstance();
            recordingController.incrementCurrentRecordingTick();
            // console.log(recordingController.getCurrentRecordingTick());
        },
        Array(100).fill(0).map((_, i) => i),
        '1i');
    sequence.start(0);
    sequence.loop = true;
    return sequence;
}


export type TempoChangeEvent = {
    position: number;
    bpm: number;
};

/**
 * Schedule tempo (BPM) changes on the Tone.js Transport timeline.
 * - Clears existing scheduled BPM automation.
 * - Sets initial BPM based on the last change at or before startAtTick.
 * - Schedules future changes at the specified tick positions.
 *
 * Returns a small handle that can clear the scheduled events.
 */
export function scheduleTempoChanges(
    tempoChanges: TempoChangeEvent[] | undefined,
    startAtTick: number = 0
): { eventIds: number[]; clear: () => void } {

    const transport = ToneJS.getTransport();
    // No changes to schedule
    if (!tempoChanges || tempoChanges.length === 0) {
        return { eventIds: [], clear: () => { } };
    }

    transport.bpm.cancelScheduledValues(0);
    transport.bpm.value = tempoChanges[0].bpm;

    // Schedule changes at/after start
    const eventIds: number[] = [];
    tempoChanges.forEach(e => {
        if (e.position < startAtTick) return;
        const id = transport.schedule((time) => {
            transport.bpm.setValueAtTime(e.bpm, time);
        }, `${e.position}i`);
        eventIds.push(id);
    });

    const clear = () => {
        eventIds.forEach(id => transport.clear(id));
        transport.bpm.cancelScheduledValues(0);
    };

    return { eventIds, clear };
}

export function playTracks(
    tracks: {
        track: RydTrack,
        container: HorizontalNoteContainer,
        instrument: RydInstrument | undefined
    }[],
    timeSignature: TimeSignature,
    tempoChanges: TempoChangeEvent[],
    caretController?: CaretController,
    options?: {
        loopPlaybackSetup?: LoopPlaybackSetup,
        recording?: boolean,
        midiAccess?
    },
): RydPlayerSession | undefined {

    if (!tracks || tracks.length === 0) {
        console.warn('No tracks to play');
        return;
    }

    const audioContext = getAudioContext();
    const ppq = getAndAssertPPQ(tracks);

    if (!audioContext) {
        console.error('AudioContext is not initialized');
        return;
    }

    // Stop any currently playing sequences
    stopSequence();

    const capturePositionSequence = options?.recording ? createCurrentTickCaptureSequence() : undefined;
    if (options?.recording) {
        const recordingController = RecordArmController.getInstance();
        // const armedTracks = tracks.filter((t) => recordingController.isTrackArmed(t.track.id));
        // console.log('would be recording armed tracks', armedTracks);

        recordingController.setCurrentRecordingTick(0);
    }

    // Schedule tempo changes
    const startAtTick = Math.max(0, options?.loopPlaybackSetup?.start?.position ?? 0);
    const longestTrackLength = Math.max(...tracks.map(t => t.container.getLength()));
    const { clear: clearTempoChanges } = scheduleTempoChanges(tempoChanges, startAtTick);

    ToneJS.getTransport().PPQ = ppq;

    // Play the metronome
    const metronomeSynth = createMetronomeSynth();
    const metronomeSequence = beatPatternToSequence(
        metronomeSynth,
        Metronome.makeMetronomeBeatSequence(timeSignature),
        ppq,
        isMetronomeMuted
    );
    // metronome loops
    metronomeSequence.loop = true;

    const caretSequence = createCaretSequence(
        ppq,
        // FIXME better if we had a project length here.
        Math.max(...tracks.map(t => t.container.getLength())),
        caretController,
    );
    caretSequence.loop = false;

    // Map each track to its sequence (assuming each track has a "grid" property and an optional "instrument")
    const trackSequences = tracks.map(track => {
        return gridToSequence(
            track.container.getGrid(true),
            track.instrument,
        );
    });

    const activeSequences: ToneJS.Sequence[] = [];
    // Then start all of them together
    trackSequences.forEach(seq => {
        seq.loop = false;
        seq.start(0);
        activeSequences.push(seq);
    });
    metronomeSequence.start(0);
    caretSequence.start(0);

    // Compute start offset and stop-at-end handling


    // Jump caret immediately to the start tick (visual sync)
    caretController?.scrollToTick(startAtTick);
    // in case we are looping during a tempo change, if there is a change scheduled, the tempo at
    // the start is not reset to the original value, so we schedule a tempo change with the starting tempo
    // matching the start of the loop.
    const initialTempoAtLoopStart = tempoChanges
    .filter(tc => tc.position <= startAtTick)
    .sort((a, b) => b.position - a.position)[0];
    if (options?.loopPlaybackSetup?.isLooping && initialTempoAtLoopStart) {
        const loopStart = options.loopPlaybackSetup.start?.position || 0;
        const loopEnd = options.loopPlaybackSetup.end?.position! || longestTrackLength;
        const loopDuration = `${loopEnd - loopStart}i`
        ToneJS.getTransport().scheduleRepeat(
            (time) => {
                console.log('setting tempo at loop start', initialTempoAtLoopStart, time);
                ToneJS.getTransport().bpm.setValueAtTime(initialTempoAtLoopStart.bpm, time);
            },
            loopDuration,
            0
        )

    }

    ToneJS.getTransport().scheduleOnce(() => {
        console.log('scheduled at start tick to set caret', startAtTick);
    }, startAtTick);

    const stopAll = () => {
        console.log('stop all');
        stopMetronome();
        clearTempoChanges();
        trackSequences.forEach(seq => {
            seq.stop();
            seq.cancel();
            seq.dispose();
        });
        capturePositionSequence?.stop();
        capturePositionSequence?.cancel();
        capturePositionSequence?.dispose();
        if (options?.recording) {
            const recordingController = RecordArmController.getInstance();
            recordingController.unsetCurrentRecordingTick();
        }
        metronomeSequence.stop();
        metronomeSequence.cancel();
        metronomeSequence.dispose();
        caretSequence.stop();
        caretSequence.cancel();
        caretSequence.dispose();
        ToneJS.getTransport().stop();
        playbackConfStore.setState({ isPlaying: false, playerSession: undefined });
    };

    if (options?.loopPlaybackSetup?.isLooping) {
        console.log('setting to loop');
        const loopStartTick = options.loopPlaybackSetup.start?.position || 0;
        const loopEndTick = options.loopPlaybackSetup.end?.position;
        ToneJS.getTransport().setLoopPoints (
            `${loopStartTick}i`,
            `${Math.min(loopEndTick || longestTrackLength, longestTrackLength)}i`
        );
        ToneJS.getTransport().loop = true;
        trackSequences.forEach(seq => { seq.loop = true; });

        caretSequence.loop = true;
    }

    ToneJS.getTransport().start(undefined, `${startAtTick}i`);

    if (options && !options.loopPlaybackSetup?.isLooping) {
        // ensure transport is stopped when the track sequneces reach the end.
        // Stop at project end relative to offset
        const remaining = Math.max(0, longestTrackLength - startAtTick);
        ToneJS.getTransport().scheduleOnce(() => {
            console.log('scheduled stop all');
            stopAll();
        }, `${remaining}i`);
    }

    return {
        sequences: activeSequences,
        start: () => ToneJS.getTransport().start(),
        pause: () => ToneJS.getTransport().pause(),
        stop: stopAll,
        isPlaying: () => ToneJS.getTransport().state === 'started',
    }
}